import PropTypes from 'prop-types'
import React, {useMemo} from 'react'
import {Box, useChakraContext} from '@chakra-ui/react3'
import useOcapiSite from '../../../hooks/useOcapiSite'
import {getSharedLibraryAssetUrl} from '../../../utils/url'
import {getDisBaseUrl} from '../../../utils/dis-utils'
import {getResponsiveImageAttributes, encodeUrlPath} from '../../../utils/responsive-image'
import {breakpoints} from '../../theme/breakpoints'

// Image format priority for DIS (lower = better compression/support)
// Supported target formats: AVIF, WebP, PNG, JPEG, GIF
// JP2 (JPEG 2000) and JXR (JPEG XR) removed due to limited browser support
const FORMAT_PRIORITY = {
    'image/avif': 1, // AVIF - best compression, modern browsers
    'image/webp': 2, // WebP - excellent compression, wide support
    'image/png': 3, // PNG - lossless, transparency support
    'image/jpeg': 4, // JPEG - universal support
    'image/gif': 5 // GIF - animation support, limited colors
}

// Map MIME types to file extensions
const FORMAT_EXTENSIONS = {
    'image/avif': 'avif',
    'image/webp': 'webp',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif'
}

/**
 * Get format priority for sorting
 * @param {string} format - MIME type
 * @returns {number} Priority value (lower = better)
 */
const getFormatPriority = (format) => FORMAT_PRIORITY[format] || 999

/**
 * Check if a format supports transparency
 * @param {string} format - Format extension or MIME type
 * @returns {boolean} True if format supports transparency
 */
const supportsTransparency = (format) => {
    // Normalize format to extension
    const ext = format.replace('image/', '').toLowerCase()
    // Formats that support transparency: AVIF, WebP, PNG, GIF
    return ['avif', 'webp', 'png', 'gif'].includes(ext)
}

/**
 * Get allowed target formats based on source format transparency support
 * @param {string} sourceFormat - Source format extension (jpg, png, gif, etc.)
 * @returns {Array<string>} Array of allowed MIME types
 */
const getAllowedFormats = (sourceFormat) => {
    const sourceSupportsTransparency = supportsTransparency(sourceFormat)

    // All supported DIS formats (JP2 and JXR removed due to limited browser support)
    const allFormats = ['image/avif', 'image/webp', 'image/png', 'image/jpeg']

    // If source supports transparency, only use formats that also support it
    if (sourceSupportsTransparency) {
        return allFormats.filter((format) => supportsTransparency(format))
    }

    // Otherwise, all formats are allowed
    return allFormats
}

/**
 * Extract original format from image URL
 * Checks for existing sfrm parameter first, then falls back to file extension
 * @param {string} url - Image URL
 * @returns {string} Original format extension (jpg, png, gif, etc.)
 */
const getOriginalFormat = (url) => {
    if (!url) return 'jpg'

    // Check for existing sfrm parameter in URL using simple string parsing
    // This works for both absolute and relative URLs
    if (url.includes('?') || url.includes('&')) {
        const queryString = url.includes('?') ? url.split('?')[1] : ''
        if (queryString) {
            const params = new URLSearchParams(queryString)
            const existingSfrm = params.get('sfrm')
            if (existingSfrm) {
                return existingSfrm
            }
        }
    }

    // Fall back to extracting from file extension
    const extension = url.split('.').pop()?.split('?')[0]?.toLowerCase()
    // DIS recognizes: gif, jp2, jpg, jpeg, png, tiff, tif
    const validExtensions = ['gif', 'jp2', 'jpg', 'jpeg', 'png', 'tiff', 'tif']
    return validExtensions.includes(extension) ? extension : 'jpg'
}

/**
 * Generate DIS URL with format conversion
 * @param {string} disBaseUrl - DIS base URL
 * @param {string} targetFormat - Target MIME type (e.g., 'image/webp')
 * @param {number} width - Image width for sw parameter
 * @param {string} originalFormat - Original source format (extracted before DIS conversion)
 * @returns {string} DIS URL with format conversion and width parameters
 */
const generateDisUrl = (disBaseUrl, targetFormat, width, originalFormat) => {
    if (!disBaseUrl) return null

    // Get target format extension
    const targetExt = FORMAT_EXTENSIONS[targetFormat] || 'jpg'

    // Replace the file extension in the URL with the target format
    // This changes the URL path to use the target format extension
    const urlWithTargetExt = disBaseUrl.replace(
        /\.(gif|jp2|jpe?g|png|tiff?)(\?|$)/i,
        `.${targetExt}$2`
    )

    // Encode URL path to handle spaces and special characters
    const encodedUrl = encodeUrlPath(urlWithTargetExt)

    // Add DIS parameters:
    // - sfrm: source format (original file extension)
    // - sw: scale width
    // - q: quality (60%)
    return `${encodedUrl}?sfrm=${originalFormat}&sw=${width}&q=60`
}

/**
 * DisImage component renders a picture element with multiple source formats and breakpoints
 * using Salesforce Dynamic Image Service (DIS) for optimized image delivery.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {string} props.src - Image source URL (can be relative or absolute Stokke URL)
 * @param {string} props.alt - Alt text for accessibility (required)
 * @param {string} [props.title] - Optional title attribute
 * @param {('eager'|'lazy')} [props.loading='lazy'] - Loading strategy
 * @param {('high'|'low'|'auto')} [props.fetchpriority] - Fetch priority hint
 * @param {Array|Object} [props.widths] - Responsive widths for srcset generation
 * @param {Array<{src: string, breakpoint?: string}>} [props.sources] - Additional sources for different breakpoints
 * @returns {JSX.Element|null} Picture element or null if DIS conversion fails
 *
 * @example
 * // Simple usage with DIS conversion
 * <DisImage
 *   src="https://dev.stokke.com/on/demandware.static/-/Library-Sites-StokkeSharedLibrary/default/dw123/image.jpg"
 *   alt="Product image"
 *   widths={[400, 800, 1200]}
 * />
 *
 * @example
 * // With breakpoint-specific sources
 * <DisImage
 *   src="cms_assets/images/fallback.jpg"
 *   alt="Hero image"
 *   sources={[
 *     { src: 'cms_assets/images/mobile.jpg', breakpoint: 'base' },
 *     { src: 'cms_assets/images/desktop.jpg', breakpoint: 's' }
 *   ]}
 *   widths={{ base: '100vw', s: '1200px' }}
 *   loading="eager"
 *   fetchpriority="high"
 * />
 */
const DisImage = ({
    src,
    alt,
    title,
    loading = 'lazy',
    fetchpriority,
    widths,
    sources,
    objectFit,
    ...rest
}) => {
    const sys = useChakraContext()
    const {sharedLibraryStatic, sharedLibraryDis} = useOcapiSite()

    // Convert src to DIS URL, or use src directly if it's already a valid URL that can't be converted
    const disBaseUrl = useMemo(() => {
        const resolvedSrc = getSharedLibraryAssetUrl(src, sharedLibraryStatic)
        const convertedUrl = getDisBaseUrl(resolvedSrc, sharedLibraryDis)

        // If conversion succeeded, use it
        if (convertedUrl) return convertedUrl

        // If conversion failed but src is a valid absolute URL, use it directly
        // This handles test URLs and other non-Stokke URLs
        try {
            new URL(resolvedSrc)
            return resolvedSrc
        } catch {
            return null
        }
    }, [src, sharedLibraryStatic, sharedLibraryDis])

    // Process breakpoint-based sources
    const processedSources = useMemo(() => {
        if (!sources || !Array.isArray(sources) || sources.length === 0) {
            // Use main src as single source
            // Extract original format from the raw src BEFORE any URL resolution
            const originalFormat = getOriginalFormat(src)
            return disBaseUrl ? [{src: disBaseUrl, breakpoint: null, originalFormat}] : []
        }

        const breakpointValues = sys.breakpoints.values || []
        const breakpointMap = Object.fromEntries(breakpointValues.map((bp) => [bp.name, bp.min]))

        return sources
            .map((source) => {
                // Extract original format from raw source BEFORE any URL resolution
                const originalFormat = getOriginalFormat(source.src)

                const resolvedSourceUrl = getSharedLibraryAssetUrl(source.src, sharedLibraryStatic)
                const disSourceUrl = getDisBaseUrl(resolvedSourceUrl, sharedLibraryDis)

                if (!disSourceUrl) return null

                return {
                    src: disSourceUrl,
                    breakpoint: source.breakpoint,
                    originalFormat
                }
            })
            .filter(Boolean)
            .sort((a, b) => {
                // Sort by breakpoint size (largest first)
                const aHasBreakpoint = a.breakpoint && a.breakpoint !== 'base'
                const bHasBreakpoint = b.breakpoint && b.breakpoint !== 'base'

                if (!aHasBreakpoint && !bHasBreakpoint) return 0
                if (!aHasBreakpoint) return 1 // No breakpoint = fallback, goes last
                if (!bHasBreakpoint) return -1

                const aMin = breakpointMap[a.breakpoint] || '0px'
                const bMin = breakpointMap[b.breakpoint] || '0px'
                return parseInt(bMin) - parseInt(aMin) // Larger first
            })
    }, [sources, disBaseUrl, src, sys.breakpoints.values, sharedLibraryStatic, sharedLibraryDis])

    // Generate responsive attributes
    const responsiveAttrs = useMemo(() => {
        if (!widths) return null
        // We need a dynamic src pattern for getResponsiveImageAttributes
        // We'll use a placeholder that we'll replace in the source generation
        return getResponsiveImageAttributes({
            src: '__DIS_BASE__[?sw={width}&q=60]',
            widths,
            breakpoints
        })
    }, [widths])

    // Generate source elements
    const sourceElements = useMemo(() => {
        const elements = []
        const breakpointValues = sys.breakpoints.values || []
        const breakpointMap = Object.fromEntries(breakpointValues.map((bp) => [bp.name, bp.min]))

        // For each processed source (different breakpoints)
        processedSources.forEach((sourceObj) => {
            // Use the original format that was extracted before DIS conversion
            const originalFormat = sourceObj.originalFormat

            // Get allowed formats based on source format transparency support
            const formats = getAllowedFormats(originalFormat)

            // For each allowed format
            formats.forEach((format) => {
                const mediaQuery =
                    sourceObj.breakpoint && sourceObj.breakpoint !== 'base'
                        ? `(min-width: ${breakpointMap[sourceObj.breakpoint]})`
                        : null

                const targetExt = FORMAT_EXTENSIONS[format] || 'jpg'

                if (responsiveAttrs) {
                    // Generate srcset from responsive attributes
                    // Encode the URL path before using it
                    const encodedSrc = encodeUrlPath(sourceObj.src)

                    // Replace placeholder with actual DIS base URL
                    let srcset = responsiveAttrs.srcSet.replace(/__DIS_BASE__/g, encodedSrc)

                    // Replace file extension with target format extension
                    srcset = srcset.replace(/\.(gif|jp2|jpe?g|png|tiff?)/gi, `.${targetExt}`)

                    // Add sfrm parameter for format conversion
                    srcset = srcset.replace(/\?sw=/g, `?sfrm=${originalFormat}&sw=`)

                    elements.push({
                        type: format,
                        srcSet: srcset,
                        sizes: responsiveAttrs.sizes,
                        media: mediaQuery
                    })
                } else {
                    // Single image without responsive widths
                    elements.push({
                        type: format,
                        srcSet: generateDisUrl(sourceObj.src, format, 1200, originalFormat),
                        media: mediaQuery
                    })
                }
            })
        })

        // Sort by format priority within same breakpoint
        return elements.sort((a, b) => {
            // If different media queries, maintain order (already sorted by breakpoint)
            if (a.media !== b.media) return 0
            // Same media query, sort by format priority
            return getFormatPriority(a.type) - getFormatPriority(b.type)
        })
    }, [processedSources, responsiveAttrs, sys.breakpoints.values])

    // Fallback img src (use original format from main src)
    const fallbackSrc = useMemo(
        () =>
            responsiveAttrs ? responsiveAttrs.src.replace('__DIS_BASE__', disBaseUrl) : disBaseUrl,
        [responsiveAttrs, disBaseUrl]
    )

    // If DIS conversion failed, render a simple img element as fallback
    if (!disBaseUrl || processedSources.length === 0) {
        return (
            <Box
                as="img"
                data-testid="dis-img"
                src={src}
                alt={alt}
                {...(title && {title})}
                loading={loading}
                {...(fetchpriority && {fetchpriority})}
                style={{width: '100%', height: '100%', objectFit}}
                {...rest}
            />
        )
    }

    return (
        <Box as="picture" data-testid="dis-picture" {...rest}>
            {sourceElements.map((source, index) => (
                <source
                    key={index}
                    data-testid={`dis-source-${source.type.replace('image/', '')}`}
                    type={source.type}
                    srcSet={source.srcSet}
                    {...(source.sizes && {sizes: source.sizes})}
                    {...(source.media && {media: source.media})}
                />
            ))}
            <img
                data-testid="dis-img"
                src={fallbackSrc}
                alt={alt}
                {...(title && {title})}
                loading={loading}
                {...(fetchpriority && {fetchpriority})}
                style={{width: '100%', height: '100%', objectFit}}
            />
        </Box>
    )
}

if (process.env.NODE_ENV === 'development') {
    DisImage.displayName = 'DisImage'
}

DisImage.propTypes = {
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
    title: PropTypes.string,
    loading: PropTypes.oneOf(['eager', 'lazy']),
    fetchpriority: PropTypes.oneOf(['high', 'low', 'auto']),
    widths: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    objectFit: PropTypes.oneOf(['contain', 'cover', 'fill', 'none', 'scale-down']),
    sources: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            breakpoint: PropTypes.string // Any breakpoint key from theme (xxs, xs, s, m, l, base)
        })
    )
}

export default DisImage
